import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { addDays, addMonths, endOfDay, endOfMonth, subDays } from 'date-fns';
import { cloneDeep } from 'lodash';
import {
  animationFrames,
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  pipe,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
  throttleTime,
  UnaryFunction,
} from 'rxjs';
import { makeDepartmentPermissionsComplete } from 'server/helpers/permissions/permissions';
import { Organisations } from 'server/shared/collections/organisations';
import { PreviousJobs } from 'server/shared/collections/previousJobs';
import { CalendarTableHelper } from 'server/shared/helpers/calendarTableHelper';
import { checkIfRested } from 'server/shared/helpers/eligibilityChecks';
import { PoolerCustomerMap } from 'server/shared/helpers/PoolerMap';
import { startOfDayUtc } from 'server/shared/helpers/startOfDayUtc';
import { AvailabilityListUser } from 'server/shared/models/availabilityListUser';
import { AvailabilityStatusEnum } from 'server/shared/models/availabilityStatusEnum';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { Organisation } from 'server/shared/models/organisation';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { PoolerUser, User } from 'server/shared/models/user';
import { UserRestWarningTypeEnum } from 'server/shared/models/userRestWarning';
import { filterOrganisationsByPermissions } from 'src/helpers/filterOrganisationsByDepartmentIds';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { queryParam$ } from '../../../../helpers/queryParam$';
import { CustomerService } from '../../../services/customer.service';
import { PermissionsService } from '../../../services/permissions.service';
import { PoolerService } from '../../../services/pooler/pooler.service';
import { UserService } from '../../../services/user.service';
import { SubscriptionService } from '../../../shared/subscription.service';
import { AvailabilitySelectionService } from './availability-selection.service';
import { getAvailabilityUsersQuery } from './getAvailabilityUsersQuery';

export type RequestState<T> = { loading: boolean; data?: T };

export type DepartmentGroup = {
  departmentDisplayName: string;
  organisation: Organisation;
  department: Department;
  hasShifts: Record<ShiftEnum, boolean>;
  renderedUserIds: Set<string>;
  displayLimitUsers: PoolerUser[];
  totalUsersCount: number;
  preparedUserById: Map<string, AvailabilityListUser>;
  canCreateJobs: boolean;
};

@Injectable()
export class AvailabilityManagementService implements OnDestroy {
  destroyed$ = new Subject<void>();

  // Observable dependencies
  poolerCustomerMap$ = this.poolerService.readyPoolerCustomerMap$;
  dispoCustomer$ = this.customerService.readyDispoCustomer$;
  loggedInUser$ = this.userService.readyDispo$;

  departmentParam$ = queryParam$(this.route, 'department');

  searchParam$ = queryParam$(this.route, 'search').pipe(debounceTime(300));

  certificatesParam$ = queryParam$(this.route, 'certificates');

  specialitiesParam$ = queryParam$(this.route, 'specialities');

  customerParam$ = queryParam$(this.route, 'restrictToCustomerIds').pipe(
    map((idsString) => (idsString ? idsString.split(',') : undefined)),
  );

  allOrganisations$ = Organisations.find({});

  sortedOrganisations$ = combineLatest([
    this.allOrganisations$,
    this.dispoCustomer$,
  ]).pipe(
    map(([allOrganisations, dispoCustomer]) => {
      return allOrganisations
        .map((organisation) => {
          let index = dispoCustomer?.organisationIds?.findIndex(
            (id) => id === organisation._id,
          );
          return { ...organisation, index };
        })
        .sort((a, b) => {
          if (a.index === -1 || a.index === undefined) {
            return 1;
          }
          if (b.index === -1 || b.index === undefined) {
            return -1;
          }
          return a.index - b.index;
        })
        .map((organisation) => {
          delete organisation.index;
          return organisation;
        });
    }),
  );

  filteredOrganisations$ = combineLatest([
    this.sortedOrganisations$,
    this.userService.dispoPermissions$,
    this.subscriptionService.organisations.ready$,
  ]).pipe(
    map(([allOrganisations, dispoPermissions]) =>
      filterOrganisationsByPermissions(
        allOrganisations,
        makeDepartmentPermissionsComplete(dispoPermissions?.departments),
      ),
    ),
  );

  rangeParam$ = queryParam$(this.route, 'range').pipe(
    map((range) => range ?? 'ALL'),
    distinctUntilChanged(),
  );

  calendarRange$: Observable<{ from: Date; to: Date }> = this.rangeParam$.pipe(
    map((range) => {
      let from: Date;
      let to: Date;

      if (range?.split(',').length === 3) {
        let splitRange = range.split(',');

        from = startOfDayUtc(new Date(splitRange[1]));
        // this should be startOfDayUtc, but the calendar shows the wrong range
        // when selecting 2025-10-17 - 2025-10-28 (missing day 28)
        to = endOfDay(new Date(splitRange[2]));
      } else {
        switch (range) {
          case 'N30':
            from = startOfDayUtc(new Date());
            to = addDays(new Date(), 30);
            break;
          case 'N90':
            from = startOfDayUtc(new Date());
            to = addDays(new Date(), 90);
            break;
          case 'L30N30':
            from = startOfDayUtc(subDays(new Date(), 30));
            to = addDays(new Date(), 30);
            break;
          case 'L90':
            from = startOfDayUtc(subDays(new Date(), 90));
            to = new Date();
            break;
          default:
            from = startOfDayUtc(new Date());
            to = endOfMonth(addMonths(new Date(), 2));
            break;
        }
      }

      return { from, to };
    }),
    shareReplay(1),
  );

  usersQuery$ = combineLatest([
    this.searchParam$,
    this.departmentParam$,
    this.certificatesParam$,
    this.specialitiesParam$,
    this.customerParam$,
    this.poolerService.readyJobSharedPoolCustomers$,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(
      ([
        search,
        department,
        certificate,
        specialities,
        restrictToCustomerIds,
        sharedPoolCustomers,
        currentCustomer,
      ]): Mongo.Selector<User> => {
        return getAvailabilityUsersQuery(
          search,
          department,
          certificate,
          specialities,
          restrictToCustomerIds,
          currentCustomer,
          sharedPoolCustomers,
        );
      },
    ),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  poolersByQuery$ = combineLatest([
    this.usersQuery$,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    switchMap(([query, customer]) =>
      this.poolerService.getPoolers(query, customer).pipe(shareReplay(1)),
    ),
    // only take poolers that have the availabilities field published ready
    map((users) => users.filter((user) => user.availabilities)),
    this.rxRemoveAvailabilitiesOutsideJoinDates(),
    throttleTime(200, undefined, { leading: true, trailing: true }),
    shareReplay(1),
  );

  rxRemoveAvailabilitiesOutsideJoinDates(): UnaryFunction<
    Observable<PoolerUser[]>,
    Observable<PoolerUser[]>
  > {
    return pipe(
      combineLatestWith(this.poolerCustomerMap$),
      map(([users, poolerCustomerMap]) =>
        users.map((user) =>
          this.removeAvailabilitiesOutsideJoinDates(user, poolerCustomerMap),
        ),
      ),
    );
  }

  removeAvailabilitiesOutsideJoinDates(
    user: PoolerUser,
    poolerCustomerMap: PoolerCustomerMap,
  ) {
    user.availabilities.map((availability) => {
      const customerOfPooler = poolerCustomerMap?.getCustomerOfUser(user._id);
      if (!this.getUserAvailable(user, availability.date, customerOfPooler)) {
        delete availability.shifts;
        availability.isAvailable = false;
      }
    });
    return user;
  }

  // Calendar table helper observable
  calendarTableHelper$ = this.calendarRange$.pipe(
    map(({ from, to }) => new CalendarTableHelper(from, to)),
    shareReplay(1),
  );

  // Accordion state
  currentCollapsedDepartmentId$ = new BehaviorSubject<string | undefined>(
    undefined,
  );

  currentCollapsedDepartment$ = combineLatest([
    this.currentCollapsedDepartmentId$.pipe(distinctUntilChanged()),
    this.filteredOrganisations$,
  ]).pipe(
    map(([currentCollapsedDepartmentId, organisations]) => {
      const organisation = organisations.find((organisation) =>
        organisation.departments.find(
          (department) => department.id === currentCollapsedDepartmentId,
        ),
      );
      const department = organisation?.departments.find(
        (department) => department.id === currentCollapsedDepartmentId,
      );
      return {
        organisation,
        department,
      };
    }),
  );

  displayListItemsCount$ = new BehaviorSubject<number>(15);
  selectedDayIndex$ = new BehaviorSubject<number | undefined>(undefined);

  selectedDay$ = combineLatest([
    this.selectedDayIndex$,
    this.calendarTableHelper$,
  ]).pipe(
    map(([selectedDayIndex, calendarTableHelper]) =>
      selectedDayIndex ? calendarTableHelper.days[selectedDayIndex] : undefined,
    ),
  );

  // selectionSummary$ moved to AvailabilityManageService
  sortedPoolers$ = combineLatest([
    this.poolersByQuery$,
    this.selectedDay$,
    this.dispoCustomer$,
    this.poolerCustomerMap$,
  ]).pipe(
    map(([poolers, selectedDay, dispoCustomer, poolerCustomerMap]) =>
      this.sortUsers(poolers, selectedDay, dispoCustomer, poolerCustomerMap),
    ),
    shareReplay(1),
  );

  baseDepartmentGroups$ = combineLatest([
    this.filteredOrganisations$,
    this.departmentParam$,
    this.permissionsService.completePermissions$,
  ]).pipe(
    map(([filteredOrganisations, departmentParam, completePermissions]) => {
      return filteredOrganisations.flatMap((organisation) =>
        organisation.departments.map((department): DepartmentGroup => {
          return {
            departmentDisplayName: `${department.name} - ${organisation.name}`,
            department,
            organisation,
            hasShifts: this.getDeparmentHasShiftsObject(department),
            renderedUserIds: new Set(),
            displayLimitUsers: [],
            totalUsersCount: 0,
            canCreateJobs:
              this.permissionsService.userHasDepartmentWritePermission(
                department.id,
                completePermissions.departments,
              ),
            preparedUserById: new Map(),
          };
        }),
      );
    }),
    shareReplay(1),
  );

  departmentGroupsWithCurrent$ = combineLatest([
    this.baseDepartmentGroups$,
    this.currentCollapsedDepartment$,
  ]).pipe(
    map(([departmentGroups, { department }]) => {
      const currentDepartmentGroup = departmentGroups.find(
        (departmentGroup) => departmentGroup.department.id === department?.id,
      );
      return {
        departmentGroups,
        currentDepartmentGroup,
      };
    }),
    shareReplay(1),
  );

  // Loader that processes users per department using animation frames
  departmentLoader$ = combineLatest([
    this.departmentGroupsWithCurrent$,
    this.calendarTableHelper$,
  ]).pipe(
    tap(([{ currentDepartmentGroup }]) => {
      if (!currentDepartmentGroup) {
        return;
      }
      currentDepartmentGroup.renderedUserIds.clear();
    }),
    switchMap(
      ([{ currentDepartmentGroup, departmentGroups }, calendarTableHelper]) => {
        if (!currentDepartmentGroup) {
          return of({ departmentGroups, loading: false });
        }
        // if we have a current department, we subscribe to more needed observables
        return combineLatest([
          this.poolerCustomerMap$,
          this.dispoCustomer$,
          this.displayListItemsCount$,
          this.sortedPoolers$,
        ]).pipe(
          switchMap(
            ([
              poolerMap,
              dispoCustomer,
              displayListItemsCount,
              sortedPoolers,
            ]) => {
              currentDepartmentGroup.totalUsersCount = sortedPoolers.length;
              currentDepartmentGroup.displayLimitUsers = sortedPoolers
                .filter((user) =>
                  this.userHasDepartment(
                    user,
                    currentDepartmentGroup.department,
                    poolerMap,
                  ),
                )
                .slice(0, displayListItemsCount);
              return animationFrames().pipe(
                map(() => ({
                  usersToLoad: currentDepartmentGroup.displayLimitUsers
                    .slice(0, displayListItemsCount)
                    .filter(
                      (user) =>
                        !currentDepartmentGroup.renderedUserIds.has(user._id),
                    ),
                })),
                takeWhile(
                  ({ usersToLoad }) => usersToLoad.length > 0,
                  true, // inclusive so it emits the final state
                ),
                map(({ usersToLoad }) => {
                  // Load one user at a time
                  if (usersToLoad[0]) {
                    const preparedUser =
                      this.prepareUserForDepartmentAvailabilityList(
                        cloneDeep(usersToLoad[0]),
                        poolerMap,
                        currentDepartmentGroup.department,
                        currentDepartmentGroup.hasShifts,
                        dispoCustomer,
                        currentDepartmentGroup.organisation,
                        calendarTableHelper,
                      );
                    currentDepartmentGroup.preparedUserById.set(
                      preparedUser._id,
                      preparedUser,
                    );
                    currentDepartmentGroup.renderedUserIds.add(
                      usersToLoad[0]._id,
                    );
                  }

                  const loading = usersToLoad.length > 1;

                  return { departmentGroups, loading };
                }),
              );
            },
          ),
        );
      },
    ),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  departmentGroups$ = combineLatest([
    this.departmentLoader$,
    this.departmentParam$,
  ]).pipe(
    map(([{ departmentGroups }, departmentParam]) => {
      if (!departmentParam) {
        return departmentGroups;
      }
      return departmentGroups.filter(
        (departmentGroup) => departmentGroup.department.id === departmentParam,
      );
    }),
    shareReplay(1),
    rxNgZoneRun(this.ngZone),
  );

  loadingDepartmentUsers$ = this.departmentLoader$.pipe(
    map(({ loading }) => loading),
  );

  sortUsers<T extends PoolerUser | AvailabilityListUser>(
    users: T[],
    selectedDay: string | undefined,
    dispoCustomer: Customer,
    poolerMap: PoolerCustomerMap,
  ): T[] {
    if (selectedDay) {
      return this.sortUsersByAvailability(users, selectedDay);
    } else {
      const prioritizeEmployeeLastName =
        dispoCustomer?.prioritizeEmployeeLastName;
      const nameSortedUsers = users?.sort((a, b) => {
        if (prioritizeEmployeeLastName) {
          return a.lastName.localeCompare(b.lastName);
        } else {
          return a.firstName.localeCompare(b.firstName);
        }
      });
      return this.sortUsersByCustomer(
        nameSortedUsers,
        dispoCustomer,
        poolerMap,
      );
    }
  }

  sortUsersByAvailability<T extends PoolerUser | AvailabilityListUser>(
    users: T[],
    selectedDay: string,
  ): T[] {
    return users
      .map((user) => ({
        user,
        availabiltiesForSelectedDay: this.countUserAvailabilitiesForDay(
          selectedDay,
          user,
        ),
      }))
      .sort((a, b) => {
        return b.availabiltiesForSelectedDay - a.availabiltiesForSelectedDay;
      })
      .map((user) => user.user);
  }

  sortUsersByCustomer<T extends PoolerUser | AvailabilityListUser>(
    users: T[],
    dispoCustomer: Customer,
    poolerMap: PoolerCustomerMap,
  ): T[] {
    return users?.sort((a, b) => {
      const aCustomer = poolerMap?.getCustomerOfUser(a._id);
      const bCustomer = poolerMap?.getCustomerOfUser(b._id);
      if (aCustomer?._id === bCustomer?._id) {
        if (dispoCustomer.prioritizeEmployeeLastName) {
          return a.lastName.localeCompare(b.lastName);
        } else {
          return a.firstName.localeCompare(b.firstName);
        }
      }
      if (aCustomer?._id === dispoCustomer?._id || !aCustomer) {
        return -1;
      }
      if (bCustomer?._id === dispoCustomer?._id || !bCustomer) {
        return 1;
      }
      if (dispoCustomer?.sharedPoolPriorities) {
        const aPriority = dispoCustomer.sharedPoolPriorities.findIndex(
          (priority) => priority.customerId === aCustomer?._id,
        );
        const bPriority = dispoCustomer.sharedPoolPriorities.findIndex(
          (priority) => priority.customerId === bCustomer?._id,
        );
        if (aPriority === -1) {
          return 1;
        } else if (bPriority === -1) {
          return -1;
        }
        return aPriority - bPriority;
      }
      return bCustomer?._id.localeCompare(aCustomer?._id);
    });
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private poolerService: PoolerService,
    private subscriptionService: SubscriptionService,
    private ngZone: NgZone,
    private customerService: CustomerService,
    private userService: UserService,
    private permissionsService: PermissionsService,
    public availabilitySelectionService: AvailabilitySelectionService,
  ) {
    this.subscriptionService.organisations.ready$
      .pipe(takeUntil(this.destroyed$), take(1))
      .subscribe(() => {
        this.subscriptionService.subscribeJobUsers();
      });
    // Reset accordion state when query changes
    this.usersQuery$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.availabilitySelectionService.resetSelection();
    });
  }

  // resetSelection() moved to AvailabilityManageService

  accordionToggled(isCollapsed: boolean, departmentId: string) {
    this.availabilitySelectionService.resetSelection();
    if (!isCollapsed) {
      this.currentCollapsedDepartmentId$.next(departmentId);
    } else {
      this.currentCollapsedDepartmentId$.next(undefined);
    }
  }

  selectDay(index: number) {
    const currentIndex = this.selectedDayIndex$.value;
    if (index === currentIndex) {
      this.selectedDayIndex$.next(undefined);
    } else {
      this.selectedDayIndex$.next(index);
    }
  }

  countUserAvailabilitiesForDay(date: string, user: PoolerUser): number {
    let count = 0;
    const availability = user.availabilities?.find(
      (availability) => availability.date === date,
    );
    if (!availability) {
      return count;
    }
    for (let shiftsKey in availability.shifts) {
      // @ts-ignore
      if (availability.shifts[shiftsKey] === AvailabilityStatusEnum.AVAILABLE) {
        count++;
      }
    }
    return count;
  }

  getDeparmentHasShiftsObject(department: Department) {
    return {
      [ShiftEnum.EARLY]: department.shifts.some(
        (shift) => shift.shift === ShiftEnum.EARLY,
      ),
      [ShiftEnum.DAY]: department.shifts.some(
        (shift) => shift.shift === ShiftEnum.DAY,
      ),
      [ShiftEnum.LATE]: department.shifts.some(
        (shift) => shift.shift === ShiftEnum.LATE,
      ),
      [ShiftEnum.NIGHT]: department.shifts.some(
        (shift) => shift.shift === ShiftEnum.NIGHT,
      ),
    };
  }

  prepareUserForDepartmentAvailabilityList(
    user: PoolerUser,
    poolerMap: PoolerCustomerMap,
    department: Department,
    departmentHasShifts: Record<ShiftEnum, boolean>,
    dispoCustomer: Customer,
    organisation: Organisation,
    calendarTableHelper: CalendarTableHelper,
  ): AvailabilityListUser {
    const customerOfUser = poolerMap?.getCustomerOfUser(user._id);

    const availabilityListUser: AvailabilityListUser = {
      ...calendarTableHelper.prepareUserForList(user),
      totalDepartmentAvailableCount: this.getTotalAvailableCountForDepartment(
        user,
        departmentHasShifts,
      ),
      totalDepartmentBookedCount: this.getTotalDepartmentBookedCount(
        user,
        department.id,
        dispoCustomer,
      ),
      totalWorkingCount: this.getTotalBookedCount(user, dispoCustomer),
      past3MonthsBookedCount: 0,
      past3MonthsBookedInOrganisationCount: 0,
    };
    if (customerOfUser?._id !== dispoCustomer?._id) {
      availabilityListUser.customerOfUser = customerOfUser;
    }
    const availabilitiesByDate = new Map(
      user.availabilities.map((a) => [a.date, a]),
    );
    availabilityListUser.availabilities.map((availability) => {
      for (let shift of department.shifts) {
        const requiredPause = customerOfUser?.requiredPause
          ? customerOfUser.requiredPause
          : 1;
        let restStatus = checkIfRested({
          user: user,
          jobDate: availability.date,
          jobShifts: [shift],
          requiredPause,
          availabilitiesByDate,
        });

        if (
          availability.shifts?.[shift.shift] ===
            AvailabilityStatusEnum.AVAILABLE &&
          !restStatus.hasRested &&
          (restStatus.restType !==
            UserRestWarningTypeEnum.POTENTIAL_SEVEN_DAY_WARNING ||
            restStatus.withBreakInfraction)
        ) {
          availability.shifts[shift.shift] = AvailabilityStatusEnum.UNAVAILABLE;
        }
      }
    });
    let previousJob = PreviousJobs.findOne({
      userId: user._id,
    });
    let previousJobOrganisation = previousJob?.organisations.find(
      (org) => org.id === organisation._id,
    );
    let previousJobDepartment = previousJobOrganisation?.departments.find(
      (dep) => dep.id === department.id,
    );
    if (previousJobDepartment) {
      previousJobOrganisation?.departments.forEach((department) => {
        availabilityListUser.past3MonthsBookedInOrganisationCount =
          availabilityListUser.past3MonthsBookedInOrganisationCount +
          department.jobs.length;
      });
    }
    return availabilityListUser;
  }

  getUserAvailable(
    user: PoolerUser,
    date: string,
    customerOfPooler?: Customer,
  ): boolean {
    if (
      customerOfPooler &&
      user.customerInformation &&
      user.customerInformation[customerOfPooler._id]
    ) {
      const customerInformation =
        user.customerInformation[customerOfPooler._id];
      const joinDate = customerInformation?.joinDate;
      const leaveDate = customerInformation?.leaveDate;
      if (joinDate && leaveDate && joinDate > leaveDate) {
        return date >= joinDate || date <= leaveDate;
      }
      return (
        (!joinDate || date >= joinDate) && (!leaveDate || date <= leaveDate)
      );
    }
    return false;
  }

  getTotalAvailableCountForDepartment(
    user: PoolerUser,
    departmentHasShifts: Record<ShiftEnum, boolean>,
  ): number {
    let count = 0;

    for (let availability of user.availabilities) {
      if (availability.isAvailable && availability.shifts) {
        for (const [key, value] of Object.entries(availability.shifts)) {
          if (
            departmentHasShifts.hasOwnProperty(key) &&
            // @ts-ignore
            departmentHasShifts[key] &&
            value === 'AVAILABLE'
          ) {
            count++;
          }
        }
      }
    }
    return count;
  }

  getTotalDepartmentBookedCount(
    user: PoolerUser,
    departmentId: string,
    dispoCustomer: Customer,
  ) {
    let count = 0;
    for (let availability of user.availabilities) {
      if (
        availability.bookedJobs &&
        Object.keys(availability.bookedJobs).length
      ) {
        const bookedJobs = Object.keys(availability.bookedJobs);
        for (let key of bookedJobs) {
          // @ts-ignore
          let job = availability.bookedJobs[key];
          if (
            job.departmentId === departmentId &&
            (job.inviteeCustomerId === dispoCustomer?._id ||
              job.organisationOwnerCustomerIds?.includes(dispoCustomer?._id) ||
              job.creatorCustomerId === dispoCustomer?._id)
          ) {
            count++;
            break;
          }
        }
      }
    }
    return count;
  }

  getTotalBookedCount(user: PoolerUser, dispoCustomer: Customer) {
    let count = 0;
    for (let availability of user.availabilities) {
      if (
        availability.bookedJobs &&
        Object.keys(availability.bookedJobs).length
      ) {
        const bookedJobs = Object.keys(availability.bookedJobs);
        for (let key of bookedJobs) {
          // @ts-ignore
          let job = availability.bookedJobs[key];
          if (
            job.inviteeCustomerId === dispoCustomer?._id ||
            job.organisationOwnerCustomerIds?.includes(dispoCustomer?._id) ||
            job.creatorCustomerId === dispoCustomer?._id
          ) {
            count++;
            break;
          }
        }
      }
    }
    return count;
  }

  userHasDepartment(
    user: PoolerUser,
    department: Department,
    poolerMap: PoolerCustomerMap,
  ): boolean {
    const customerOfUser = poolerMap?.getCustomerOfUser(user._id);

    if (!customerOfUser) {
      return false;
    }
    const customerUserInformation =
      user.customerInformation?.[customerOfUser._id];
    if (!customerUserInformation) {
      return false;
    }

    const userHasDepartment = customerUserInformation.departmentIds?.some(
      (departmentId) => departmentId === department.id,
    );
    const departmentBlacklisted =
      user.userPreferenceDepartmentBlacklist?.includes(department.id) ||
      department?.blacklistedUsers?.find(
        (blacklistedUser) => blacklistedUser.id === user._id,
      );

    if (customerOfUser.enableTags) {
      const organisation = Organisations.findOne({
        'departments.id': department.id,
      });
      const oraganisationTags =
        organisation.customerInformation?.[customerOfUser._id]?.tags;
      if (oraganisationTags?.length) {
        const userHasTag = !!customerUserInformation.tags?.filter((tag) =>
          oraganisationTags.map((orgTag) => orgTag.id).includes(tag.id),
        ).length;

        return (
          (userHasDepartment && !departmentBlacklisted && userHasTag) || false
        );
      }
    }
    return (userHasDepartment && !departmentBlacklisted) || false;
  }

  displayMoreEntries() {
    this.hasMoreEntries$()
      .pipe(take(1))
      .subscribe((hasMore) => {
        if (hasMore) {
          const currentCount = this.displayListItemsCount$.value;
          this.displayListItemsCount$.next(currentCount + 20);
        }
      });
  }

  hasMoreEntries$(): Observable<boolean> {
    return combineLatest([
      this.departmentGroupsWithCurrent$,
      this.displayListItemsCount$,
    ]).pipe(
      map(([organisationGroupsData, currentDisplayCount]) => {
        const { currentDepartmentGroup } = organisationGroupsData;
        if (!currentDepartmentGroup) return false;

        return currentDisplayCount < currentDepartmentGroup.totalUsersCount;
      }),
    );
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  updateQueryParams(queryParams: { [key: string]: string | null }) {
    this.router
      .navigate([], {
        queryParamsHandling: 'merge',
        queryParams,
      })
      .then();
  }
}
